import { useMemo } from 'react'

import { Culture } from '@/types/culture'
import { Treatment } from '@/types/treatment'

import { useCompanies } from './use-companies'

export interface AccessControlProps {
  culture: Culture
  treatment: Treatment
}

interface ImageTypeMapper {
  value: string
  label: string
}

interface DefaultValueMapper {
  label: string
  enabled: boolean
}

type AccessControl = Record<
  string,
  | (DefaultValueMapper & {
      treatments: Record<
        string,
        | (DefaultValueMapper & {
            imagesTypes: {
              mosaic?: ImageTypeMapper[]
              raw_images?: ImageTypeMapper[]
            }
          })
        | undefined
      >
    })
  | undefined
>

function useAccessControl({ culture, treatment }: AccessControlProps) {
  const { permissions, selectedCompany } = useCompanies()

  const accessControl = useMemo(() => {
    const hasWeedAccess = !!permissions?.WeedAccess

    const raw_images = selectedCompany.raw_image && [
      { value: 'raw_images', label: 'Sem geotags' },
      {
        value: 'raw_images_geotag',
        label: 'Com geotags',
      },
    ]

    const orthomosaic = {
      value: 'mosaic',
      label: 'Ortomosaicos',
    }

    return {
      cane: {
        label: 'Cana de açúcar',
        enabled: selectedCompany.cane && hasWeedAccess,
        treatments: {
          weed: {
            label: 'Daninhas',
            enabled: true,
            imagesTypes: {
              mosaic: [orthomosaic],
              raw_images,
            },
          },
          planting_failure: {
            label: 'Falha de plantio',
            enabled: selectedCompany.planting_failure,
            imagesTypes: {
              mosaic: [orthomosaic],
              raw_images,
            },
          },
        },
      },
      soybean: {
        label: 'Soja',
        enabled: selectedCompany.soybean,
        treatments: {
          weed: {
            label: 'Daninhas',
            enabled: hasWeedAccess,
            imagesTypes: {
              mosaic: [orthomosaic],
              raw_images,
            },
          },
          physiology: {
            label: 'Desempenho fisiológico',
            enabled: !hasWeedAccess,
            imagesTypes: {
              raw_images,
            },
          },
        },
      },
    } as AccessControl
  }, [selectedCompany, culture, treatment])

  const currentCulture = useMemo(
    () => accessControl[culture],
    [culture, accessControl],
  )

  const currentTreatment = useMemo(
    () => currentCulture?.treatments[treatment],
    [treatment, currentCulture],
  )

  return {
    accessControl,
    currentCulture,
    currentTreatment,
    isPhysiology: !permissions?.WeedAccess,
  }
}

export default useAccessControl
